import merge from 'lodash/merge';
import { useMemo } from 'react';

import { PageSectionTypes } from '~/app/components/ItemPage/sections/types';
import { ItemPageLayoutDefinition } from '~/app/components/ItemPage/types';
import { SelectedCustomPage } from '~/app/lib/store/types';

import {
  AddonTypes,
  ItemPageLayoutSettings,
  LayoutSettingsAddon,
} from '~/app/lib/songwhipApi/types';

import { CustomPageItemContext } from './types';

const DEFAULT_LAYOUT: ItemPageLayoutDefinition['main'] = [
  {
    component: PageSectionTypes.PAGE_TITLE,
  },
];

const useCustomPageContext = ({
  customPage,
  originalItemContext,
}: {
  customPage: SelectedCustomPage;
  originalItemContext?: CustomPageItemContext;
}) => {
  const config = customPage?.config;
  const addonsResolved = config?.addons || {};

  const defaultDesktopImage =
    addonsResolved.CUSTOM_IMAGE?.desktop?.url || customPage.image;

  const defaultMobileImage =
    addonsResolved.CUSTOM_IMAGE?.default?.url || customPage.image;

  // merge default layout settings with custom
  const layoutSettingsResolved = merge<
    ItemPageLayoutSettings,
    LayoutSettingsAddon
  >(
    toDefaultLayoutSettings({
      mobileImage: defaultMobileImage,
      desktopImage: defaultDesktopImage,
    }),
    addonsResolved[AddonTypes.LAYOUT_SETTINGS] || {}
  );

  return useMemo<CustomPageItemContext>(
    () => ({
      presets: {},

      data: {
        item: customPage,
        custom: config?.objects,
      },

      layout: {
        main: toLayout(customPage),
        settings: layoutSettingsResolved,
      },

      config,
      addons: addonsResolved,
      originalItemContext,
    }),
    [customPage, originalItemContext]
  );
};

const toLayout = (customPage: SelectedCustomPage) => {
  if (customPage.config?.layout && customPage.config?.version !== 2) {
    return toLayoutFromLegacyFormat(customPage);
  }

  // use custom layout if defined, otherwise use the default layout
  return customPage.config?.layout?.main || DEFAULT_LAYOUT;
};

const toLayoutFromLegacyFormat = (customPage: SelectedCustomPage) => {
  const customLayout = customPage.config?.layout?.main;

  // if no custom layout is provided, use the default layout
  if (!customLayout?.length) {
    return DEFAULT_LAYOUT;
  }

  // prepend the title to the custom layout
  return [...DEFAULT_LAYOUT.slice(0, 1), ...customLayout];
};

const toDefaultLayoutSettings = ({
  mobileImage,
  desktopImage,
}: {
  mobileImage: string | undefined;
  desktopImage: string | undefined;
}): ItemPageLayoutSettings => {
  return {
    desktopLayout: 'desktopSingleColumn',

    mobileSingleColumn: {
      image: {
        url: mobileImage,

        layout: {
          type: 'fade',
          spaceAboveContent: 'large',
        },
      },
    },

    desktopSingleColumn: {
      image: {
        url: desktopImage,

        layout: {
          type: 'fill',
          spaceAboveContent: 'none',
        },
      },
    },

    desktopTwoColumn: {
      alignContent: 'right',

      image: {
        url: desktopImage,

        layout: {
          type: 'half-fill',
        },
      },
    },
  };
};

export default useCustomPageContext;
